import { useMemo, useState } from "react";
import { Alert, Box, Button, CircularProgress, Paper, TableBody, TextField, Typography } from "@mui/material";
import { CheckCircle as CheckCircleIcon } from "@mui/icons-material";
import { CeldaKit, CeldaPrincipal, Columna, FilaKit, TablaKit } from "../../components/kit/TablaKit";
import Etiqueta from "../../components/kit/Etiqueta";
import { useCohortes, useSetFechaCohorte } from "../../queries/useCoordinacion";
import { Cohorte } from "../../types/coordinacion";
import { tiempoRestante } from "../../utils/fecha";
import { Accesores, ordenar, useOrden } from "../../utils/orden";

const COLUMNAS: (string | Columna)[] = [
  { label: "Etapa", sortKey: "etapa" },
  { label: "Procesos", sortKey: "total" },
  { label: "Fecha de entrega", sortKey: "fecha" },
  { label: "Fecha de corrección", sortKey: "correccion" },
  { label: "Acción", align: "right" as const },
];

const ACC_COHORTE: Accesores<Cohorte> = {
  etapa: (c) => c.paso,
  total: (c) => c.total,
  fecha: (c) => c.fecha,
  correccion: (c) => c.fecha_correccion,
};

/**
 * "Fechas de entrega" (coordinador): los procesos actuales agrupados por etapa y
 * período. Fijar una fecha crea/actualiza el CalendarioPaso y la aplica a todos
 * los procesos de esa cohorte. Solo staff (el backend responde 403 al resto).
 */
export default function Coordinacion() {
  const { data, isLoading, isError } = useCohortes();
  const cohortes = useMemo(() => data ?? [], [data]);
  const { orden, onSort } = useOrden();

  // Agrupa por período preservando el orden del backend (período desc, etapa asc).
  const grupos = useMemo(() => {
    const map = new Map<string, { label: string; cohortes: Cohorte[] }>();
    for (const c of cohortes) {
      const key = `${c.ano}.${c.semestre}`;
      if (!map.has(key)) map.set(key, { label: `${c.ano} · ${c.semestre_display}`, cohortes: [] });
      map.get(key)!.cohortes.push(c);
    }
    return [...map.values()];
  }, [cohortes]);

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 0.5 }}>
        Fechas de entrega
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Fija, por etapa y período, la fecha de entrega del estudiante y el plazo de corrección de los revisores. Se aplica a todos los
        procesos de esa cohorte.
      </Typography>

      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {isError && <Alert severity="error">No se pudieron cargar las cohortes (¿tienes permisos de coordinación?).</Alert>}

      {!isLoading && !isError && cohortes.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: "center", border: "1px solid", borderColor: "divider", borderRadius: 2 }}>
          <Typography color="text.secondary">No hay procesos registrados.</Typography>
        </Paper>
      )}

      {grupos.map((g) => (
        <Box key={g.label} sx={{ mb: 3.5 }}>
          <Typography variant="h6" sx={{ mb: 1.5 }}>
            {g.label}
          </Typography>
          <TablaKit columnas={COLUMNAS} minWidth={760} orden={orden} onSort={onSort}>
            <TableBody>
              {ordenar(g.cohortes, ACC_COHORTE, orden).map((c) => (
                <FilaCohorte key={`${c.paso_id}-${c.ano}-${c.semestre}`} c={c} />
              ))}
            </TableBody>
          </TablaKit>
        </Box>
      ))}
    </Box>
  );
}

// Un input de fecha/hora con el "cuánto falta" debajo.
function CeldaFecha({ valor, onChange, iso }: { valor: string; onChange: (v: string) => void; iso: string | null }) {
  const restante = tiempoRestante(iso);
  const vencido = restante === "vencido";
  return (
    <CeldaKit>
      <TextField type="datetime-local" size="small" value={valor} onChange={(e) => onChange(e.target.value)} sx={{ width: 215 }} />
      {restante && (
        <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: vencido ? "error.main" : "text.secondary" }}>
          ({restante})
        </Typography>
      )}
    </CeldaKit>
  );
}

function FilaCohorte({ c }: { c: Cohorte }) {
  const [fecha, setFecha] = useState(c.fecha_local ?? "");
  const [fechaCorr, setFechaCorr] = useState(c.fecha_correccion_local ?? "");
  const mut = useSetFechaCohorte();
  const dirty = fecha !== (c.fecha_local ?? "") || fechaCorr !== (c.fecha_correccion_local ?? "");

  const guardar = () =>
    mut.mutate({ paso_id: c.paso_id, ano: c.ano, semestre: c.semestre, fecha: fecha || null, fecha_correccion: fechaCorr || null });

  return (
    <FilaKit>
      <CeldaPrincipal>{c.paso}</CeldaPrincipal>
      <CeldaKit>
        <Etiqueta variante="contorno">{c.total}</Etiqueta>
      </CeldaKit>
      <CeldaFecha valor={fecha} onChange={setFecha} iso={c.fecha} />
      <CeldaFecha valor={fechaCorr} onChange={setFechaCorr} iso={c.fecha_correccion} />
      <CeldaKit align="right">
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 1 }}>
          {mut.isSuccess && !dirty && <CheckCircleIcon fontSize="small" color="success" />}
          <Button size="small" variant="contained" disabled={!dirty || mut.isPending} onClick={guardar}>
            {mut.isPending ? <CircularProgress size={16} color="inherit" /> : "Guardar"}
          </Button>
        </Box>
      </CeldaKit>
    </FilaKit>
  );
}
